import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  // PayPal returns `?token=<ORDER_ID>&PayerID=…`. The 100%-off/free coupon
  // path still returns `?session_id=<local_id>`. Support both.
  const sessionId = params.get("session_id") || params.get("token");
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
        if (attempts.current >= 8) { setState("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 8) { setState("timeout"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, refresh]);

  return (
    <div className="mx-auto max-w-md px-6 py-24 text-center" data-testid="checkout-success-page">
      {state === "polling" && (
        <>
          <Loader2 className="h-10 w-10 mx-auto text-zinc-900 animate-spin" />
          <div className="mt-6 font-display text-3xl">Finalising your payment…</div>
          <p className="text-sm text-zinc-500 mt-2">Hang tight — confirming with PayPal.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
          <div className="mt-6 font-display text-3xl" data-testid="checkout-success-state-paid">Payment confirmed.</div>
          <p className="text-sm text-zinc-500 mt-2">A receipt has been emailed to you. Your account is updated.</p>
          <div className="mt-8 flex items-center gap-2 justify-center">
            <Button asChild className="btn-brand"><Link to="/dashboard">Go to dashboard</Link></Button>
            <Button asChild variant="outline"><Link to="/dashboard/invoices">View invoice</Link></Button>
          </div>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle className="h-12 w-12 mx-auto text-red-600" />
          <div className="mt-6 font-display text-3xl" data-testid="checkout-success-state-failed">
            {state === "timeout" ? "Still processing." : "Payment not completed."}
          </div>
          <p className="text-sm text-zinc-500 mt-2">
            {state === "timeout" ? "We'll keep finalising in the background. Check invoices in a minute." : "No charges were made. Try again from the pricing page."}
          </p>
          <div className="mt-8 flex items-center gap-2 justify-center">
            <Button asChild className="btn-brand"><Link to="/pricing">Back to pricing</Link></Button>
            <Button variant="outline" onClick={() => nav("/dashboard")}>Dashboard</Button>
          </div>
        </>
      )}
      {info && (
        <div className="mt-10 text-xs text-zinc-400 font-mono-tag uppercase">session · {sessionId?.slice(0, 12)}…</div>
      )}
    </div>
  );
}
